import { MAP } from '../utils/constants';

/**
 * Inset from the full block cell (meters). Same for mesh + collision.
 * Larger = more grass strip around each tower (easier to drive the setback).
 */
export const BUILDING_LOT_PAD = 58;

export function getBlockWidth(): number {
  const roadWithSidewalk = MAP.ROAD_WIDTH + MAP.SIDEWALK_WIDTH * 2;
  const totalRoadSpace = (MAP.GRID + 1) * roadWithSidewalk;
  const inner = MAP.SIZE - totalRoadSpace;
  return inner / MAP.GRID;
}

/**
 * Open park lots: no building in these cells (grass interior). All other cells get towers.
 * Minimap fills the same cells in green. ~18% parks keeps the city mostly built-up.
 */
const PARK_LOT_THRESHOLD_PPM = 180; // (h % 1000) < this → park; 180/1000 = 18%

export function isParkLot(ix: number, iz: number): boolean {
  const G = MAP.GRID;
  if (ix < 0 || iz < 0 || ix >= G || iz >= G) return false;
  let h = (ix * 374761393 + iz * 668265263 + 0x9e3779b9) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return (h >>> 0) % 1000 < PARK_LOT_THRESHOLD_PPM;
}

/**
 * Block interior (not road strips): which grid cell (ix, iz) contains world (x, z), if any.
 */
export function tryBlockCellAtWorldXZ(
  x: number,
  z: number,
): { ix: number; iz: number } | null {
  const { HALF, GRID, ROAD_WIDTH, SIDEWALK_WIDTH } = MAP;
  const roadWithSidewalk = ROAD_WIDTH + SIDEWALK_WIDTH * 2;
  const totalRoadSpace = (GRID + 1) * roadWithSidewalk;
  const inner = MAP.SIZE - totalRoadSpace;
  const blockW = inner / GRID;
  const span = blockW + roadWithSidewalk;

  for (let iz = 0; iz < GRID; iz++) {
    const zmin = -HALF + roadWithSidewalk + iz * span;
    const zmax = zmin + blockW;
    if (z < zmin || z > zmax) continue;
    for (let ix = 0; ix < GRID; ix++) {
      const xmin = -HALF + roadWithSidewalk + ix * span;
      const xmax = xmin + blockW;
      if (x >= xmin && x <= xmax) {
        return { ix, iz };
      }
    }
  }
  return null;
}

/**
 * Building footprint in world XZ — must stay aligned with `getBuildingAABBs()` in collision.ts.
 * Park lots: no building → always false.
 */
export function isPointInsideBuildingFootprint(
  x: number,
  z: number,
  ix: number,
  iz: number,
): boolean {
  if (isParkLot(ix, iz)) return false;
  const { HALF, GRID, ROAD_WIDTH, SIDEWALK_WIDTH } = MAP;
  const roadWithSidewalk = ROAD_WIDTH + SIDEWALK_WIDTH * 2;
  const totalRoadSpace = (GRID + 1) * roadWithSidewalk;
  const inner = MAP.SIZE - totalRoadSpace;
  const blockW = inner / GRID;
  const halfW = (blockW - BUILDING_LOT_PAD) * 0.5;
  const cx =
    -HALF + roadWithSidewalk + blockW / 2 + ix * (blockW + roadWithSidewalk);
  const cz =
    -HALF + roadWithSidewalk + blockW / 2 + iz * (blockW + roadWithSidewalk);
  return (
    x >= cx - halfW &&
    x <= cx + halfW &&
    z >= cz - halfW &&
    z <= cz + halfW
  );
}

/** On block grass but not under a tower footprint (park lot or setback ring). */
export function isWorldXZOnGrassWithoutBuilding(x: number, z: number): boolean {
  const cell = tryBlockCellAtWorldXZ(x, z);
  if (!cell) return false;
  return !isPointInsideBuildingFootprint(x, z, cell.ix, cell.iz);
}

export function cellCenter(
  ix: number,
  iz: number,
  blockW: number,
  roadWidth: number,
  half: number,
): { x: number; z: number } {
  const roadWithSidewalk = roadWidth + MAP.SIDEWALK_WIDTH * 2;
  const x =
    -half + roadWithSidewalk + blockW / 2 + ix * (blockW + roadWithSidewalk);
  const z =
    -half + roadWithSidewalk + blockW / 2 + iz * (blockW + roadWithSidewalk);
  return { x, z };
}

/** Full block interior (grass + tower pad), aligned with roads — for fences / minimap. */
export function getBlockCellOuterBounds(
  ix: number,
  iz: number,
  blockW: number,
  roadWidth: number,
  half: number,
  grid: number,
): { minX: number; maxX: number; minZ: number; maxZ: number } | null {
  if (ix < 0 || iz < 0 || ix >= grid || iz >= grid) return null;
  const roadWithSidewalk = roadWidth + MAP.SIDEWALK_WIDTH * 2;
  const span = blockW + roadWithSidewalk;
  const minX = -half + roadWithSidewalk + ix * span;
  const minZ = -half + roadWithSidewalk + iz * span;
  return { minX, maxX: minX + blockW, minZ, maxZ: minZ + blockW };
}

/** Tower collision footprint (no building on park lots). */
export function getTowerFootprintBounds(
  ix: number,
  iz: number,
  blockW: number,
  roadWidth: number,
  half: number,
): { minX: number; maxX: number; minZ: number; maxZ: number } | null {
  if (isParkLot(ix, iz)) return null;
  const halfW = (blockW - BUILDING_LOT_PAD) * 0.5;
  const c = cellCenter(ix, iz, blockW, roadWidth, half);
  return {
    minX: c.x - halfW,
    maxX: c.x + halfW,
    minZ: c.z - halfW,
    maxZ: c.z + halfW,
  };
}
